// src/validation/mediationLifecycle.js
// WP4 — Mediation participation & outcome per slot (spec §2b `phases.mediation`).
//
// GROUND TRUTH (token research 2026-07-29): there is NO proven GAM→buyer token
// in MITM traffic — the actual mediation bid happens server-side (GAM ↔ buyer
// server) and never crosses the wire. On-device we only ever observe a buyer
// SDK's own telemetry/config/error traffic (`sdk_bidding` rows) and, on a win,
// its render beacons. So this module renders PARTICIPATION + OUTCOME, not a
// bid-by-bid waterfall, across exactly three provable-on-device states:
//   - invited  — buyer id is present in the GAM row's a3pBuyers list
//   - fired    — an sdk_bidding row for that buyer landed within the timing
//                window around this GAM request
//   - won      — the GAM response's adapterLabel names that buyer
//
// `confidence` is ALWAYS the literal string 'inferred' — never 'proven' (no
// hard token) and never used to affect `overallStatus`/`issues` upstream; the
// a3p per-buyer matcher itself is unverified (validateGAMRequest.js a3p
// comment), so this stays informational-only per spec WP4.
//
// Pure. Never throws — degrades to the empty/neutral shape on any bad input,
// matching the validator convention used across this codebase.

// Known SDK-Bidding buyer id → display name, mirrored from mediationHealth.js's
// BUYERS registry (kept separate/duplicated intentionally — WP4 must not edit
// or import from mediationHealth.js's session-global aggregator, which powers
// a different panel; ids match proxy-server/mediationNetworks.js `net.id`).
// Adapter-label words that name a buyer by its SDK rather than its buyer id.
const ADAPTER_ALIASES = { facebook: 'meta', fan: 'meta', audiencenetwork: 'meta', vungle: 'liftoff' };

const GENERIC_NAME_WORDS = new Set(['network', 'networks', 'ads', 'ad', 'audience', 'monetize', 'exchange', 'media', 'sdk', 'mediation', 'adapter']);

const BUYER_NAMES = {
  liftoff: 'Liftoff Monetize (Vungle)',
  applovin: 'AppLovin',
  meta: 'Meta Audience Network',
  mintegral: 'Mintegral',
  pangle: 'Pangle',
  unity: 'Unity Ads',
  inmobi: 'InMobi',
  dt_exchange: 'DT Exchange (Fyber)',
  bidmachine: 'BidMachine',
  moloco: 'Moloco Ads',
  ly_ads: 'LY Ads (Line)',
};

// Timing window around the GAM request within which an sdk_bidding row is
// considered "fired for this slot". Mediation SDKs are typically invoked by
// the mediation waterfall just before the GAM ad call resolves, so most
// buyer traffic lands shortly BEFORE the GAM request; a short AFTER window
// covers late/async telemetry (error logs, render beacons on the win path,
// SDKs that report after the ad call returns). Not symmetric: -3s/+10s.
const WINDOW_BEFORE_MS = 3000;
const WINDOW_AFTER_MS = 10000;

// A billing notice (validData.kind 'bill') is the GMA SDK pinging the winning
// SDK-bidding buyer's bill URL on render — proof of the RENDERER. It carries no
// ad-unit id, and several slots load within the same second, so a plain window
// would hand one win to every nearby slot. Bills therefore attach only to the
// NEAREST GAM request at-or-before them (gamTimestamps), within a longer window:
// a full-screen ad bills when shown, which can trail the request.
const BILL_WINDOW_AFTER_MS = 60000;

function isBill(row) {
  const k = (row?.validData || {}).kind;
  return k === 'bill' || k === 'impression'; // either names the renderer
}
function isBillNotice(row) {
  return (row?.validData || {}).kind === 'bill';
}

function nearestGamAtOrBefore(ts, gamTimestamps) {
  let best = 0;
  for (const t of gamTimestamps) if (t > 0 && t <= ts && t > best) best = t;
  return best;
}

const toMs = (v) => {
  if (!v && v !== 0) return 0;
  if (typeof v === 'number') return v;
  const n = Date.parse(v);
  return Number.isNaN(n) ? 0 : n;
};

// Same shape as AdLifecycleView.jsx's local `ts()` helper — duplicated here
// so this module stays dependency-free/pure and independently testable.
function rowTs(r) {
  return (
    toMs(r?.startedAt) ||
    toMs(r?.startedAtMs) ||
    toMs(r?.timing?.start) ||
    toMs(r?.startedAtISO) ||
    toMs(r?.timestamp) ||
    0
  );
}

function buyerNameFor(id, fallbackName) {
  return BUYER_NAMES[id] || fallbackName || id;
}

// Resolve a stable buyer id from an sdk_bidding row's validData. Falls back
// to a normalized networkName, then 'unknown' — never throws, never null/undef.
function buyerIdForRow(row) {
  const vd = row?.validData || {};
  if (vd.networkId) return String(vd.networkId).toLowerCase();
  if (vd.networkName) return String(vd.networkName).toLowerCase().replace(/\s+/g, '_');
  return 'unknown';
}

/**
 * Build the WP4 mediation participation+outcome phase for one GAM slot.
 *
 * @param {Object} params
 * @param {Object} params.gamRow          the GAM request row for this slot
 * @param {number} [params.gamTs]         GAM row timestamp (ms); derived from gamRow if omitted
 * @param {Array}  [params.sdkBiddingRows] all captured rows already filtered to type 'sdk_bidding'
 * @returns {{fired: boolean, confidence: 'inferred', buyers: Array, winner: (string|null), reasons: string[]}}
 */
export function buildMediationPhase({ gamRow, gamTs, sdkBiddingRows, gamTimestamps } = {}) {
  const EMPTY = { fired: false, confidence: 'inferred', buyers: [], winner: null, winnerSource: null, reasons: [] };
  try {
    if (!gamRow) return EMPTY;

    const anchorTs = typeof gamTs === 'number' && gamTs > 0 ? gamTs : rowTs(gamRow);
    const rows = Array.isArray(sdkBiddingRows) ? sdkBiddingRows : [];

    const expanded = gamRow.validData?.expanded || {};
    const a3pBuyers = Array.isArray(expanded.a3pBuyers)
      ? expanded.a3pBuyers.map((b) => String(b).toLowerCase())
      : [];
    const a3pSet = new Set(a3pBuyers);
    const a3pUnknown = Array.isArray(gamRow.issues)
      && gamRow.issues.some((i) => i && i.code === 'ingest_url_truncated');

    const adapterLabel = expanded.adapterLabel || null;
    // Resolve the winner to a buyer id when the adapterLabel names a known
    // buyer (matches a3p ids or a known display name via shared word tokens);
    // otherwise fall back to a normalized version of the label itself so
    // `winner` is still set.
    let winner = null;
    if (adapterLabel) {
      const norm = String(adapterLabel).toLowerCase();
      const normWords = new Set(norm.split(/[^a-z0-9]+/).filter(Boolean));
      const byId = [...a3pSet].find((id) => norm.includes(id));
      // iOS adapter classes name the SDK, not the buyer: GADMediationAdapterFacebook
      // → "Facebook" is Meta Audience Network (STRIKEAD-4893 iOS HAR, 2026-10-17).
      const alias = Object.keys(ADAPTER_ALIASES).find((w) => normWords.has(w));
      if (byId) {
        winner = byId;
      } else if (alias) {
        winner = ADAPTER_ALIASES[alias];
      } else {
        const byName = Object.entries(BUYER_NAMES).find(([, name]) => {
          // Generic words ("network", "ads" …) are not a buyer name: "AdMob Network"
          // matched "Meta Audience Network" on "network".
          const nameWords = name.toLowerCase().split(/[^a-z0-9]+/).filter((w) => w && !GENERIC_NAME_WORDS.has(w));
          return nameWords.some((w) => normWords.has(w));
        });
        winner = byName ? byName[0] : norm.replace(/\s+/g, '_');
      }
    }

    // Attribute sdk_bidding rows to this slot by timing window, then group
    // per buyer id.
    const attributed = new Map(); // buyerId -> { rows: [], name }
    const gamTsList = Array.isArray(gamTimestamps) ? gamTimestamps : [];
    if (anchorTs > 0) {
      for (const row of rows) {
        const rTs = rowTs(row);
        if (!rTs) continue;
        // A buyer SDK's settings sync (kind 'config': Meta adnw_sync2, sent once at
        // launch) is app-wide, not this slot's bid: inside the window it made Meta
        // read "fired" on an AdX-won slot (Android, 21 Oct).
        if (((row && row.validData) || {}).kind === 'config') continue;
        const delta = rTs - anchorTs; // >0 means fired after the GAM request
        if (isBill(row)) {
          if (delta < 0 || delta > BILL_WINDOW_AFTER_MS) continue;
          // only the slot whose request came last before the bill gets the win
          if (gamTsList.length && nearestGamAtOrBefore(rTs, gamTsList) !== anchorTs) continue;
        } else if (delta < -WINDOW_BEFORE_MS || delta > WINDOW_AFTER_MS) continue;

        const id = buyerIdForRow(row);
        const vd = row.validData || {};
        if (!attributed.has(id)) {
          attributed.set(id, { rows: [], name: vd.networkName || null });
        }
        const entry = attributed.get(id);
        entry.rows.push(row);
        if (!entry.name && vd.networkName) entry.name = vd.networkName;
      }
    }

    const reasons = [];

    // The GAM response's adapter is authoritative when we have it (MITM). Without
    // a response (adb logcat, trimmed bodies), a billing notice names the winner.
    let winnerSource = winner ? 'adapter' : null;
    const billedBy = [...attributed.entries()]
      .filter(([, e]) => e.rows.some(isBill))
      .map(([id]) => id);
    if (!winner && billedBy.length === 1) {
      winner = billedBy[0];
      // 'bill' when a billing notice fired, else the buyer's own impression beacon.
      winnerSource = attributed.get(winner).rows.some(isBillNotice) ? 'bill' : 'impression';
    } else if (winner && billedBy.length && !billedBy.includes(winner)) {
      reasons.push(`GAM response names ${adapterLabel}, but the billing notice came from ${billedBy.map((id) => buyerNameFor(id)).join(', ')}`);
    } else if (!winner && billedBy.length > 1) {
      reasons.push(`billing notices from several buyers near this request (${billedBy.map((id) => buyerNameFor(id)).join(', ')}) — renderer ambiguous`);
    }

    const buyerIds = new Set([...a3pSet, ...attributed.keys()]);
    if (winner) buyerIds.add(winner);

    const buyers = [...buyerIds].map((id) => {
      const entry = attributed.get(id) || null;
      const reachedViaA3p = a3pSet.has(id);
      const firedForBuyer = !!entry && entry.rows.length > 0;

      const name = buyerNameFor(id, entry?.name);
      const rowKeys = firedForBuyer
        ? entry.rows.map((r) => r._k ?? r.id).filter((k) => k != null)
        : [];
      const bidCount = firedForBuyer
        ? entry.rows.filter((r) => (r.validData || {}).kind === 'bid').length
        : 0;

      // Outcome: prefer an observed 'filled' bid, else the strongest signal
      // from any attributed row, else not_observed when never fired.
      let outcome = 'not_observed';
      if (firedForBuyer) {
        const outcomes = entry.rows.map((r) => (r.validData || {}).outcome).filter(Boolean);
        if (outcomes.includes('won')) outcome = 'won';
        else if (outcomes.includes('filled')) outcome = 'filled';
        else if (outcomes.includes('error')) outcome = 'error';
        else if (outcomes.includes('no_fill')) outcome = 'no_fill';
        else outcome = outcomes[0] || 'unknown';
      }

      let matchedBy;
      if (firedForBuyer && reachedViaA3p) matchedBy = 'a3p+timing';
      else if (firedForBuyer) matchedBy = 'timing';
      else matchedBy = 'adapter'; // invited-but-silent or winner-only entries

      // QA reasons — real findings, not narration.
      if (reachedViaA3p && !firedForBuyer) {
        reasons.push(`${name}: invited via a3p but never fired`);
      }
      // A logcat GAM URL is cut at ~4KB inside cust_params, so a3p (later in the
      // URL) is lost: the invite list is UNKNOWN there, not empty.
      if (firedForBuyer && !reachedViaA3p && !a3pUnknown) {
        reasons.push(`${name}: fired but not invited (not in a3p buyer list)`);
      }
      if (winner === id && winnerSource === 'adapter' && !firedForBuyer) {
        reasons.push(`adapterLabel=${adapterLabel} but no ${name} sdk_bidding traffic observed (won but silent)`);
      }

      return {
        id,
        name,
        outcome,
        bidCount,
        reachedViaA3p,
        rowKeys,
        matchedBy,
      };
    });

    return {
      fired: buyers.some((b) => b.rowKeys.length > 0),
      confidence: 'inferred',
      buyers,
      winner,
      winnerSource,
      reasons,
    };
  } catch {
    return EMPTY;
  }
}

export default { buildMediationPhase };
